import { useState, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { Sparkles, Download } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";

import { Button } from "@/components/ui/button";
import { TuviLaSoBoard } from "@/components/tuvi/tuvi-la-so-board";
import { BaziBoard } from "@/components/bazi/bazi-board";
import { HexagramFullBoard } from "@/components/iching/hexagram-full-board";
import {
  shareImageUrl,
  type SharedResource,
  type TuviChart,
  type BaziChart,
} from "@/lib/boide-api";
import { composeShareImage, downloadBlob } from "@/lib/share-image";
import { toast } from "sonner";
import { AiWatermark } from "@/components/ai-watermark";

/** Trang xem công khai 1 quẻ / lá số — hiển thị ảnh BE render (giống board FE);
 * nếu ảnh lỗi thì render board React thật làm fallback. Download tải ảnh BE. */
export function SharedView({
  resource,
  token,
}: {
  resource: SharedResource | null;
  token?: string;
}) {
  const [downloading, setDownloading] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);

  // Tải ảnh CỐ ĐỊNH (bản chụp desktop giống hệt FE, do BE lưu). Lỗi → chụp board
  // đang hiển thị (theo layout thiết bị hiện tại) làm dự phòng.
  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const filename =
        rtype === "tuvi"
          ? "la-so-tu-vi.png"
          : rtype === "bazi"
            ? "la-so-bat-tu.png"
            : "que-kinh-dich.png";
      let blob: Blob | null = null;
      if (token) {
        try {
          // cache:"no-store" tránh dùng bản cache no-cors của <img> (fetch cors sẽ fail).
          const res = await fetch(shareImageUrl(token, resource?.image_version), {
            cache: "no-store",
          });
          if (res.ok) blob = await res.blob();
        } catch {
          /* rơi xuống chụp board */
        }
      }
      if (!blob && boardRef.current) {
        blob = await composeShareImage(boardRef.current);
      }
      if (blob) downloadBlob(blob, filename);
      else throw new Error("no image");
    } catch (e) {
      console.error("Lỗi tải ảnh:", e);
      toast.error(`Lỗi tải ảnh: ${(e as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  if (!resource) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center">
        <h1 className="font-serif-soft text-2xl text-gold-gradient">Link không khả dụng</h1>
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">
          Link chia sẻ này không tồn tại hoặc đã được người tạo tắt đi.
        </p>
        <Button asChild className="mt-6">
          <Link to="/">Về trang chủ Bói Đê</Link>
        </Button>
      </div>
    );
  }

  const rtype = resource.resource_type;

  // Lời luận — chỉ có khi người chia sẻ bật "Kèm lời luận giải" (BE mới trả về).
  const readingSections: Array<{ key: string; label?: string; content: string }> = [];
  if (resource.resource_type === "tuvi") {
    const reading = resource.data.reading_data;
    if (reading?.dai_cuc)
      readingSections.push({ key: "dai_cuc", label: "Đại cục", content: reading.dai_cuc });
    for (const s of reading?.cungs ?? []) {
      if (s?.content_md)
        readingSections.push({ key: s.key, label: s.label, content: s.content_md });
    }
  } else if (resource.resource_type === "bazi") {
    const reading = resource.data.reading_data;
    if (reading?.dai_cuc)
      readingSections.push({ key: "dai_cuc", label: "Đại cục", content: reading.dai_cuc });
    for (const s of reading?.sections ?? []) {
      if (s?.content_md)
        readingSections.push({ key: s.key, label: s.label, content: s.content_md });
    }
  } else if (resource.resource_type === "iching" && resource.data.interpretation) {
    readingSections.push({
      key: "interpretation",
      label: "Lời thầy luận",
      content: resource.data.interpretation,
    });
  }

  const meta = {
    iching: {
      alt: "Quẻ Kinh Dịch",
      caption: "Tự gieo quẻ Kinh Dịch và luận giải vận mệnh của riêng bạn.",
      to: "/kinh-dich" as const,
      cta: "Gieo quẻ ngay",
      download: "Tải ảnh quẻ",
    },
    tuvi: {
      alt: "Lá số Tử Vi",
      caption: "Tự lập lá số Tử Vi và khai mở vận mệnh của riêng bạn.",
      to: "/tu-vi" as const,
      cta: "Lập lá số ngay",
      download: "Tải ảnh lá số",
    },
    bazi: {
      alt: "Lá số Bát Tự",
      caption: "Tự lập lá số Bát Tự, Tứ Trụ và khai mở vận mệnh của riêng bạn.",
      to: "/bat-tu" as const,
      cta: "Lập lá số ngay",
      download: "Tải ảnh lá số",
    },
  }[rtype];

  return (
    <div className="flex-1 overflow-y-auto w-full">
      <div className="flex min-h-[80vh] flex-col items-center justify-center gap-6 px-4 py-10 pb-24 md:pb-10">
        {/* Board React THẬT — responsive: mobile ra mobile, desktop ra desktop.
            Ảnh cố định (OG preview / tải về) là bản chụp riêng, xem handleDownload. */}
        <div ref={boardRef} className="w-full max-w-4xl px-2">
          {resource.resource_type === "tuvi" ? (
            <TuviLaSoBoard apiChart={resource.data as TuviChart} maskPii={resource.mask_pii} />
          ) : resource.resource_type === "bazi" ? (
            <BaziBoard chart={resource.data as BaziChart} maskPii={resource.mask_pii} />
          ) : (
            <HexagramFullBoard hex={resource.data.hexagram_result} />
          )}
        </div>

        {readingSections.length > 0 && (
          <section className="w-full max-w-3xl rounded-2xl border border-gold-soft/40 bg-background/40 px-5 py-4">
            <h2 className="font-serif-soft text-lg text-gold-gradient">Lời luận giải</h2>
            <AiWatermark className="mb-3 mt-1" showNoRefund={false} />
            <div className="space-y-4">
              {readingSections.map((s) => (
                <div key={s.key}>
                  {s.label && <h3 className="mb-1 text-sm font-semibold text-gold">{s.label}</h3>}
                  <div className="[&_p]:my-1.5 [&_strong]:text-foreground [&_h1]:text-base [&_h2]:text-base [&_h3]:text-sm [&_h3]:font-semibold text-sm leading-relaxed text-foreground/90">
                    <ReactMarkdown remarkPlugins={MARKDOWN_REMARK_PLUGINS}>
                      {s.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="flex flex-col items-center gap-3 text-center">
          <p className="max-w-sm text-sm italic text-muted-foreground">{meta.caption}</p>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full justify-center px-4">
            <Button asChild className="w-full sm:w-auto">
              <Link to={meta.to}>
                <Sparkles className="mr-2 h-4 w-4" />
                {meta.cta}
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={handleDownload}
              disabled={downloading}
              className="w-full sm:w-auto border-gold-soft text-gold hover:bg-gold-soft/10"
            >
              <Download className="mr-2 h-4 w-4" />
              {downloading ? "Đang xử lý..." : meta.download}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
